import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  ArrowRight,
  MapPin,
  ClipboardCheck,
  ScrollText,
  MessageSquare,
  CalendarRange,
  FileText,
  AlertOctagon,
  Camera,
  Clock,
  Building2,
  Users,
  CheckCircle2,
  ShieldCheck,
  Send,
  Download,
  Lock,
  Layers,
  Mail,
  Loader2,
  Sun,
  Moon,
  PlayCircle,
  User,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import BrandMark from "../components/BrandMark";

const LANDING_CSS = `
@import url("https://fonts.googleapis.com/css2?family=Archivo:wght@600;700;800;900&family=Inter:wght@400;500;600;700&display=swap");

.ss-land {
  --l-bg: #F5F8FB; /* [paleta-azul] */
  --l-alt: #E8EEF5;
  --l-surface: #FFFFFF;
  --l-ink: #0F1B2D;
  --l-muted: #56637A;
  --l-line: #D5DEE9;
  --l-accent: #EC5D2A;
  --l-accent-dark: #D44E1F;
  font-family: "Inter", ui-sans-serif, system-ui, sans-serif;
  background-color: var(--l-bg);
  color: var(--l-ink);
}
.dark .ss-land {
  --l-bg: #0F0F0F;
  --l-alt: #171615;
  --l-surface: #1B1A19;
  --l-ink: #F4F1EC;
  --l-muted: #A9A39A;
  --l-line: #2E2B28;
}
.l-bg { background-color: var(--l-bg); }
.l-alt { background-color: var(--l-alt); }
.l-surface { background-color: var(--l-surface); }
.l-muted { color: var(--l-muted); }
.l-line { border-color: var(--l-line); }
.l-display {
  font-family: "Archivo", "Inter", ui-sans-serif, system-ui, sans-serif;
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: 1.04;
}
.l-btn { background-color: var(--l-accent); color: #FFFFFF; }
.l-btn:hover { background-color: var(--l-accent-dark); }
.l-ghost { border: 1px solid var(--l-ink); color: var(--l-ink); }
.l-ghost:hover { background-color: var(--l-ink); color: var(--l-bg); }
.l-chip { border: 1px solid var(--l-line); color: var(--l-muted); }
.l-chip:hover { border-color: var(--l-ink); color: var(--l-ink); }
.l-cell-off { background-color: var(--l-bg); border: 1px solid var(--l-line); }
.l-cell-off:hover { border-color: var(--l-ink); }
.l-field {
  background-color: var(--l-surface);
  color: var(--l-ink);
  border: 1px solid var(--l-line);
}
.l-field::placeholder { color: var(--l-muted); }
.l-field:focus { border-color: var(--l-accent); box-shadow: 0 0 0 3px rgba(236, 93, 42, 0.22); }
.l-hover:hover { color: var(--l-accent); }

@keyframes ss-pulse { 0% { transform: scale(0.5); opacity: 0.9; } 100% { transform: scale(2.4); opacity: 0; } }
@keyframes ss-slide { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: none; } }
@keyframes ss-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
.ss-pulse { animation: ss-pulse 2s ease-out infinite; }
.ss-slide { animation: ss-slide 0.45s ease-out both; }
.ss-float { animation: ss-float 7s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .ss-pulse, .ss-slide, .ss-float { animation: none; }
}

/* [estilo-suave] Mude --l-radius para deixar os cantos mais ou menos arredondados (0px = retos). */
.ss-land { --l-radius: 18px; }
.ss-land .rounded-sm { border-radius: 12px; }
.ss-land .l-btn, .ss-land .l-ghost, .ss-land .l-chip { border-radius: 999px; }
.ss-land div.l-surface:not(.fixed), .ss-land form.l-surface { border-radius: min(var(--l-radius), 28%); }
.ss-land .l-field, .ss-land .l-cell-off { border-radius: 12px; }
.ss-photo { border-radius: var(--l-radius); }
/* [pontilhado-degrade] bolinhas que vao sumindo de cima para baixo (a mascara so afeta as bolinhas) */
.ss-dots { position: relative; isolation: isolate; }
.ss-dots::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background-image: radial-gradient(circle at 1px 1px, rgba(15, 27, 45, 0.22) 1.4px, transparent 0);
  background-size: 24px 24px;
  -webkit-mask-image: linear-gradient(to bottom, #000 0%, rgba(0, 0, 0, 0.55) 35%, transparent 80%);
  mask-image: linear-gradient(to bottom, #000 0%, rgba(0, 0, 0, 0.55) 35%, transparent 80%);
}
.dark .ss-land .ss-dots::before { background-image: radial-gradient(circle at 1px 1px, rgba(255, 255, 255, 0.16) 1.4px, transparent 0); }
.ss-mark-word { color: var(--l-accent); }
`;

/* ---------- imagens ----------
   Coloque suas fotos de obra na pasta public/images com estes nomes.
   Se a foto nao existir, a pagina tenta a imagem de reserva e, no fim,
   mostra uma ilustracao de obra desenhada no proprio codigo. */
const UNSPLASH_HERO =
  "https://images.unsplash.com/photo-1762146828422-50a8bd416d3c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwYXJjaGl0ZWN0dXJlJTIwYmx1ZXByaW50fGVufDB8fHx8MTc4MzA3NzY2MHww&ixlib=rb-4.1.0&q=85&w=1600";
const UNSPLASH_SUPERVISOR =
  "https://images.unsplash.com/photo-1672748341520-6a839e6c05bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODMwNzc2NTl8MA&ixlib=rb-4.1.0&q=85&w=900";
const UNSPLASH_WORKER =
  "https://images.unsplash.com/photo-1646227655685-a530813759b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODMwNzc2NTl8MA&ixlib=rb-4.1.0&q=85&w=900";

const IMG = {
  hero: ["/images/hero.jpg", UNSPLASH_HERO],
  onsite: ["/images/onsite.jpg"],
  office: ["/images/office.jpg"],
  client: ["/images/client.jpg"],
  worker: ["/images/worker.jpg", UNSPLASH_WORKER],
  supervisor: ["/images/supervisor.jpg", UNSPLASH_SUPERVISOR],
};

/* ---------- dados ---------- */

const NAV_ITEMS = [
  { id: "tour", label: "Product tour" },
  { id: "benefits", label: "Benefits" },
  { id: "how-it-works", label: "How it works" },
  { id: "roles", label: "Roles" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "Contact us" },
];

const SPECS = [
  { icon: Lock, k: "Multi tenant", v: "Isolated company workspaces" },
  { icon: ShieldCheck, k: "Role based", v: "Worker, supervisor, admin" },
  { icon: MapPin, k: "GPS geofence", v: "Attendance bound to the site" },
  { icon: FileText, k: "PDF reports", v: "One click export" },
];

const FEED_EVENTS = [
  { icon: Clock, tag: "Attendance", text: "Crew member clocked in on Level 1" },
  { icon: ClipboardCheck, tag: "Inspection", text: "ITP checkpoint 2.3 signed off as Pass" },
  { icon: Camera, tag: "Evidence", text: "Photo added to checkpoint 1.1.1" },
  { icon: ScrollText, tag: "Daily log", text: "Daily log submitted for Ground Floor" },
  { icon: AlertOctagon, tag: "NCR", text: "NCR raised on a failed checkpoint" },
  { icon: MessageSquare, tag: "Chat", text: "New message in the jobsite chat" },
];

const PILLARS = [
  {
    picto: "city",
    title: "One focus",
    text: "We build for construction teams and nothing else, so every screen matches how a jobsite really runs.",
  },
  {
    picto: "laptop",
    title: "One platform",
    text: "Crews, supervisors and admins work from the same record, each with the access they need.",
  },
  {
    picto: "badge",
    title: "One record",
    text: "Inspections, attendance and logs live together, so reporting is one click instead of a chase.",
  },
];

const TABS = [
  {
    id: "attendance",
    label: "Attendance",
    icon: Clock,
    title: "Clock in with GPS, not with guesswork",
    text: "Workers clock in and out from the jobsite and every entry is tied to the site they picked. Time tracking is ready for the office without chasing paper sheets.",
    bullets: ["GPS clock in and clock out", "Timesheets per worker and per site", "Live view of who is on site now"],
  },
  {
    id: "inspections",
    label: "Inspections",
    icon: ClipboardCheck,
    title: "Digital ITPs with a live status ring",
    text: "Build an Inspection and Test Plan once, clone it to any project and sign it off in the field. Every answer updates the status instantly. Try it on the right.",
    bullets: ["Pass, Fail and N/A on every checkpoint", "Photos and comments on the evidence", "One click PDF report for the client"],
  },
  {
    id: "logs",
    label: "Daily logs",
    icon: ScrollText,
    title: "The day on site, recorded properly",
    text: "Field notes and photos are captured as the work happens, scoped to the jobsite, so the record is there when someone asks what happened on a given day.",
    bullets: ["Notes and photos in one entry", "Scoped to the active jobsite", "Searchable history for the office"],
  },
  {
    id: "chat",
    label: "Chat",
    icon: MessageSquare,
    title: "A chat that belongs to the jobsite",
    text: "Each jobsite has its own live conversation, so instructions and confirmations stay with the project instead of getting lost in personal phones.",
    bullets: ["Live messaging per jobsite", "Admin monitoring when needed", "Everything stays inside your company"],
  },
  {
    id: "roster",
    label: "Roster",
    icon: CalendarRange,
    title: "Plan the week and publish it",
    text: "Build the roster, assign shifts and let the crew see what is coming up. Click the cells on the right to plan a week.",
    bullets: ["Shift planning by crew and day", "Workers see their upcoming shifts", "Connected to time tracking"],
  },
];

const STORIES = [
  {
    kicker: "On site",
    title: "Know who is on site and what is done",
    text: "Crews clock in with GPS, answer every checkpoint with photo evidence and log the day from their phone. No paper sheets, no end of week catch up.",
    srcs: IMG.onsite,
    art: "skyline",
    cta: "See attendance in the tour",
    link: "tour",
  },
  {
    kicker: "In the office",
    title: "Watch every inspection as it happens",
    text: "Supervisors and admins see the status of each ITP the moment it changes, follow up on failed checkpoints and raise NCRs without leaving the console.",
    srcs: IMG.office,
    art: "blueprint",
    cta: "See inspections in the tour",
    link: "tour",
  },
  {
    kicker: "With the client",
    title: "Hand over reports without chasing paper",
    text: "Export a clean PDF of any inspection with the status chart, every answer, photos and comments. The record is already complete when the client asks for it.",
    srcs: IMG.client,
    art: "report",
    cta: "Talk to our team",
    link: "contact",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Set up your company",
    text: "Create your company, add your jobsites and invite your team with a simple invite link. Each company is fully separated from the others.",
  },
  {
    n: "02",
    title: "Build your plans",
    text: "Create ITP templates with sections, items and response types. Clone them to any project and as many times as you need.",
  },
  {
    n: "03",
    title: "Run it in the field",
    text: "Workers clock in, complete inspections with photos and log the day. Supervisors and admins see the status as it happens.",
  },
  {
    n: "04",
    title: "Report in one click",
    text: "Export a clean PDF of any inspection with status chart, answers, photos and comments, ready to send to the client.",
  },
];

const ROLES = {
  Worker: {
    icon: Users,
    summary: "Everything a crew member needs on site, nothing more.",
    points: [
      "Clock in and out with GPS",
      "Answer ITP checkpoints with Pass, Fail or N/A",
      "Attach photos and write comments",
      "Log the day and talk with the crew",
      "See upcoming shifts",
    ],
  },
  Supervisor: {
    icon: ShieldCheck,
    summary: "Stay on top of the work while it is being done.",
    points: [
      "Review inspections as they happen",
      "Manage the roster and shifts",
      "Monitor time tracking",
      "Raise and follow NCRs",
      "Export PDF reports",
    ],
  },
  Admin: {
    icon: Lock,
    summary: "Full control of the company, users and templates.",
    points: [
      "Create ITP templates and clone them to projects",
      "Manage users, access and invites",
      "Configure jobsites and company settings",
      "Upload your own fillable PDF forms",
      "Monitor chat and company wide activity",
    ],
  },
};

const FAQ = [
  {
    q: "How do my workers join?",
    a: "You invite them with an invite link. They create their account and land directly in your company, with the access level you chose.",
  },
  {
    q: "Is my company data separated from other companies?",
    a: "Yes. SiteSync is multi tenant, so every company has its own isolated workspace, users, jobsites and records.",
  },
  {
    q: "Can I use my own forms?",
    a: "Yes. Admins can upload fillable PDF forms. Workers fill them in, save multiple entries and email the filled PDF from the field.",
  },
  {
    q: "What do I get when I export an inspection?",
    a: "A printable report with the inspection details, the status chart, every checkpoint with its answer, photos and comments.",
  },
  {
    q: "Does it work on a phone?",
    a: "Yes. The console runs in any modern browser, on desktop or on a phone, so the same login works at the office and on site.",
  },
];

const TOPICS = ["Request a demo", "General question", "Pricing", "Support"];

const CONTACT_POINTS = [
  { icon: Layers, text: "A walkthrough tailored to your trade and your inspection plans" },
  { icon: Building2, text: "Help setting up your company, jobsites and first ITP template" },
  { icon: Mail, text: "A reply from our team within one business day" },
];

/* ---------- pecas pequenas ---------- */

function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

function Reveal({ children, delay = 0, className = "" }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        inView ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

// [marca-sitesync] O antigo losango (hexagono) foi trocado pelo simbolo da empresa (BrandMark).

function Kicker({ children }) {
  // [estilo-suave] pilula com o simbolo da empresa (a cor da borda acompanha a cor do texto)
  return (
    <div
      className="inline-flex items-center gap-2.5 rounded-full border py-1.5 pl-2 pr-4"
      style={{ borderColor: "color-mix(in srgb, currentColor 22%, transparent)" }}
    >
      <BrandMark size={22} />
      <span className="text-[13px] font-semibold tracking-wide">{children}</span>
    </div>
  );
}

function Wordmark({ light = false }) {
  return (
    <span className="flex items-center gap-2.5">
      <BrandMark size={32} building={light ? "#F4F1EC" : "var(--l-ink)"} />
      <span className="l-display text-[26px]" style={light ? { color: "#F4F1EC" } : undefined}>
        SiteSync
      </span>
    </span>
  );
}

/* ---------- ilustracoes de reserva (quando nao ha foto) ---------- */

function ArtSkyline() {
  return (
    <svg
      viewBox="0 0 800 500"
      preserveAspectRatio="xMidYMid slice"
      className="absolute inset-0 h-full w-full"
      aria-hidden="true"
    >
      <defs>
        <pattern id="ss-win" width="24" height="30" patternUnits="userSpaceOnUse">
          <rect x="6" y="7" width="11" height="15" style={{ fill: "var(--l-alt)", opacity: 0.4 }} />
        </pattern>
      </defs>
      <rect width="800" height="500" style={{ fill: "var(--l-alt)" }} />
      <rect x="610" y="60" width="44" height="44" transform="rotate(45 632 82)" style={{ fill: "var(--l-accent)" }} />
      <rect x="92" y="96" width="24" height="24" transform="rotate(45 104 108)" style={{ fill: "var(--l-ink)", opacity: 0.3 }} />
      <g style={{ fill: "var(--l-ink)" }}>
        <rect x="40" y="250" width="170" height="250" />
        <rect x="230" y="170" width="140" height="330" />
        <rect x="560" y="210" width="170" height="290" />
        <rect x="390" y="300" width="150" height="200" style={{ opacity: 0.88 }} />
      </g>
      <g fill="url(#ss-win)">
        <rect x="40" y="250" width="170" height="250" />
        <rect x="230" y="170" width="140" height="330" />
        <rect x="560" y="210" width="170" height="290" />
        <rect x="390" y="300" width="150" height="200" />
      </g>
      <g fill="none" strokeWidth="4" style={{ stroke: "var(--l-accent)" }}>
        <line x1="456" y1="84" x2="456" y2="300" />
        <line x1="478" y1="84" x2="478" y2="300" />
        <polyline points="456,300 478,268 456,236 478,204 456,172 478,140 456,108" />
        <line x1="300" y1="84" x2="690" y2="84" strokeWidth="8" />
        <line x1="640" y1="84" x2="640" y2="190" strokeWidth="2" />
        <rect x="628" y="190" width="24" height="18" />
        <rect x="296" y="70" width="34" height="28" style={{ fill: "var(--l-accent)" }} />
      </g>
    </svg>
  );
}

function ArtBlueprint() {
  return (
    <svg
      viewBox="0 0 800 500"
      preserveAspectRatio="xMidYMid slice"
      className="absolute inset-0 h-full w-full"
      aria-hidden="true"
    >
      <defs>
        <pattern id="ss-bp" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" strokeWidth="1" style={{ stroke: "var(--l-line)" }} />
        </pattern>
      </defs>
      <rect width="800" height="500" style={{ fill: "var(--l-alt)" }} />
      <rect width="800" height="500" fill="url(#ss-bp)" />
      <g fill="none" strokeWidth="6" style={{ stroke: "var(--l-ink)" }}>
        <rect x="120" y="90" width="560" height="320" />
        <polyline points="120,250 360,250 360,90" />
        <polyline points="360,330 560,330 560,410" />
        <line x1="560" y1="250" x2="680" y2="250" />
      </g>
      <g style={{ fill: "var(--l-accent)" }}>
        <circle cx="240" cy="170" r="26" />
        <circle cx="470" cy="190" r="26" />
        <circle cx="620" cy="340" r="26" />
      </g>
      <g fill="none" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" stroke="#FFFFFF">
        <polyline points="228,170 238,181 254,160" />
        <polyline points="458,190 468,201 484,180" />
        <polyline points="608,340 618,351 634,330" />
      </g>
    </svg>
  );
}

function ArtReport() {
  const path = "M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831";
  return (
    <svg
      viewBox="0 0 800 500"
      preserveAspectRatio="xMidYMid slice"
      className="absolute inset-0 h-full w-full"
      aria-hidden="true"
    >
      <rect width="800" height="500" style={{ fill: "var(--l-alt)" }} />
      <rect x="230" y="40" width="340" height="430" style={{ fill: "var(--l-surface)", stroke: "var(--l-line)" }} strokeWidth="2" />
      <rect x="262" y="76" width="140" height="14" style={{ fill: "var(--l-ink)" }} />
      <rect x="262" y="104" width="90" height="8" style={{ fill: "var(--l-muted)", opacity: 0.6 }} />
      <g transform="translate(262 140) scale(4.4)">
        <g transform="rotate(-90 18 18)">
          <path d={path} fill="none" strokeWidth="3.8" style={{ stroke: "var(--l-line)" }} />
          <path d={path} fill="none" strokeWidth="3.8" strokeDasharray="62 100" style={{ stroke: "#22c55e" }} />
          <path d={path} fill="none" strokeWidth="3.8" strokeDasharray="14 100" strokeDashoffset="-62" style={{ stroke: "#ef4444" }} />
        </g>
      </g>
      <g style={{ fill: "var(--l-line)" }}>
        <rect x="262" y="320" width="276" height="10" />
        <rect x="262" y="344" width="240" height="10" />
        <rect x="262" y="368" width="276" height="10" />
        <rect x="262" y="392" width="180" height="10" />
      </g>
      <rect x="470" y="410" width="68" height="40" style={{ fill: "var(--l-accent)" }} />
      <rect x="600" y="80" width="40" height="40" transform="rotate(45 620 100)" style={{ fill: "var(--l-accent)" }} />
    </svg>
  );
}

const ART = { skyline: ArtSkyline, blueprint: ArtBlueprint, report: ArtReport };

function Photo({ srcs, alt = "", className = "", art }) {
  const [idx, setIdx] = useState(0);
  const failed = idx >= srcs.length;
  return (
    <div className={`ss-photo l-alt relative overflow-hidden ${className}`}>
      {failed ? (
        art
      ) : (
        <img
          key={srcs[idx]}
          src={srcs[idx]}
          alt={alt}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          onError={() => setIdx((i) => i + 1)}
        />
      )}
    </div>
  );
}

function DiamondCollage() {
  const diamond = { borderRadius: "24%", overflow: "hidden" };
  return (
    <div className="relative mx-auto aspect-square w-full max-w-lg">
      <div className="absolute left-[14%] top-[2%] h-[72%] w-[72%]" style={diamond}>
        <Photo srcs={IMG.hero} art={<ArtSkyline />} className="h-full w-full" />
      </div>
      <div className="absolute left-[-2%] top-[8%] h-[40%] w-[40%]" style={diamond}>
        <Photo srcs={IMG.supervisor} art={<ArtBlueprint />} className="h-full w-full" />
      </div>
      <div className="absolute bottom-0 right-0 h-[46%] w-[46%]" style={diamond}>
        <Photo srcs={IMG.worker} art={<ArtReport />} className="h-full w-full" />
      </div>
      <span className="absolute bottom-[30%] left-[18%] h-4 w-4 rounded-full bg-[#EC5D2A]" />
      <span className="absolute right-[12%] top-[14%] h-3 w-3 rounded-full bg-[#6B6B6B]" />
    </div>
  );
}

/* ---------- pictogramas dos pilares ---------- */

function Picto({ kind }) {
  if (kind === "city") {
    return (
      <svg viewBox="0 0 120 100" className="h-20 w-24" aria-hidden="true">
        <rect x="28" y="12" width="42" height="78" style={{ fill: "var(--l-accent)" }} />
        <rect x="8" y="46" width="24" height="44" fill="none" strokeWidth="6" style={{ stroke: "var(--l-ink)" }} />
        <rect x="70" y="40" width="28" height="50" style={{ fill: "var(--l-accent)" }} />
        <path d="M102 90 V34 L110 20 L118 34 V90" fill="none" strokeWidth="6" style={{ stroke: "var(--l-ink)" }} />
        <g style={{ fill: "var(--l-ink)" }}>
          <rect x="38" y="26" width="8" height="8" />
          <rect x="52" y="26" width="8" height="8" />
          <rect x="38" y="44" width="8" height="8" />
          <rect x="52" y="44" width="8" height="8" />
          <rect x="78" y="54" width="8" height="8" />
        </g>
      </svg>
    );
  }
  if (kind === "laptop") {
    return (
      <svg viewBox="0 0 120 100" className="h-20 w-24" aria-hidden="true">
        <rect x="22" y="12" width="76" height="52" rx="4" fill="none" strokeWidth="7" style={{ stroke: "var(--l-ink)" }} />
        <rect x="30" y="20" width="60" height="36" rx="2" style={{ fill: "var(--l-accent)" }} />
        <rect x="8" y="70" width="104" height="14" rx="3" style={{ fill: "var(--l-ink)" }} />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 120 100" className="h-20 w-24" aria-hidden="true">
      <circle cx="60" cy="50" r="44" style={{ fill: "var(--l-accent)" }} />
      <circle cx="60" cy="50" r="28" fill="none" strokeWidth="6" style={{ stroke: "var(--l-ink)" }} />
      <polyline points="48,50 57,59 74,40" fill="none" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" style={{ stroke: "var(--l-ink)" }} />
    </svg>
  );
}

/* ---------- janelas de demonstracao ---------- */

function Donut({ pass, fail, na, total, size = 112 }) {
  const path = "M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831";
  const p = total ? (pass / total) * 100 : 0;
  const f = total ? (fail / total) * 100 : 0;
  const n = total ? (na / total) * 100 : 0;
  const inspected = pass + fail + na;
  const trans = { transition: "stroke-dasharray 0.5s ease, stroke-dashoffset 0.5s ease" };
  return (
    <div className="relative shrink-0" style={{ height: size, width: size }}>
      <svg className="h-full w-full -rotate-90" viewBox="0 0 36 36">
        <path stroke="currentColor" style={{ color: "var(--l-line)" }} strokeWidth="3.8" fill="none" d={path} />
        <path
          stroke="#22c55e"
          strokeWidth="3.8"
          fill="none"
          d={path}
          strokeDasharray={`${p} 100`}
          strokeDashoffset="0"
          style={trans}
        />
        <path
          stroke="#ef4444"
          strokeWidth="3.8"
          fill="none"
          d={path}
          strokeDasharray={`${f} 100`}
          strokeDashoffset={`${-p}`}
          style={trans}
        />
        <path
          stroke="#8a8a8a"
          strokeWidth="3.8"
          fill="none"
          d={path}
          strokeDasharray={`${n} 100`}
          strokeDashoffset={`${-(p + f)}`}
          style={trans}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-mono text-lg font-bold">
          {inspected}/{total}
        </span>
        <span className="l-muted text-[9px] font-mono uppercase tracking-widest">Inspected</span>
      </div>
    </div>
  );
}

function WindowFrame({ title, children }) {
  return (
    <div className="l-surface l-line w-full overflow-hidden border">
      <div className="l-alt l-line flex items-center gap-2 border-b px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-red-500/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-500/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-green-500/80" />
        <span className="l-muted ml-3 truncate font-mono text-[10px] uppercase tracking-widest">{title}</span>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function LivePanel() {
  const [tick, setTick] = useState(0);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const feed = setInterval(() => setTick((t) => t + 1), 2600);
    const clock = setInterval(() => setNow(new Date()), 1000);
    return () => {
      clearInterval(feed);
      clearInterval(clock);
    };
  }, []);

  const visible = [0, 1, 2].map((k) => FEED_EVENTS[(tick + k) % FEED_EVENTS.length]);
  const onSite = 12 + (tick % 3);
  const openItps = 8 - (tick % 4);

  return (
    <div className="ss-float w-full shadow-2xl">
      <WindowFrame title="sitesync console / live">
        <div className="flex items-center justify-between">
          <div>
            <div className="l-muted font-mono text-[10px] font-bold uppercase tracking-[0.2em]">Jobsite</div>
            <div className="mt-1 text-base font-semibold">Level 1 Wet Areas</div>
          </div>
          <div className="text-right">
            <div className="font-mono text-lg font-bold" style={{ color: "var(--l-accent)" }}>
              {now.toLocaleTimeString()}
            </div>
            <div className="flex items-center justify-end gap-1.5 font-mono text-[10px] uppercase tracking-widest text-green-600">
              <span className="relative flex h-2 w-2">
                <span className="ss-pulse absolute inline-flex h-full w-full rounded-full bg-green-500" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
              </span>
              Live
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          <div className="l-bg l-line border p-3">
            <div className="l-muted font-mono text-[9px] uppercase tracking-widest">On site</div>
            <div className="mt-1 font-mono text-2xl font-bold">{onSite}</div>
          </div>
          <div className="l-bg l-line border p-3">
            <div className="l-muted font-mono text-[9px] uppercase tracking-widest">Open ITPs</div>
            <div className="mt-1 font-mono text-2xl font-bold">{openItps}</div>
          </div>
          <div className="l-bg l-line border p-3">
            <div className="l-muted font-mono text-[9px] uppercase tracking-widest">Logs today</div>
            <div className="mt-1 font-mono text-2xl font-bold">{3 + (tick % 2)}</div>
          </div>
        </div>

        <div className="l-muted mt-4 font-mono text-[10px] font-bold uppercase tracking-[0.2em]">Activity feed</div>
        <div className="mt-2 space-y-2">
          {visible.map((ev, idx) => {
            const Icon = ev.icon;
            return (
              <div
                key={idx === 0 ? `first-${tick}` : `row-${idx}-${ev.text}`}
                className={`flex items-center gap-3 border px-3 py-2 ${idx === 0 ? "ss-slide" : "l-bg l-line"}`}
                style={idx === 0 ? { borderColor: "var(--l-accent)", backgroundColor: "rgba(236,93,42,0.08)" } : undefined}
              >
                <Icon className="h-4 w-4 shrink-0" style={{ color: idx === 0 ? "var(--l-accent)" : "var(--l-muted)" }} />
                <div className="min-w-0 flex-1 truncate text-xs">{ev.text}</div>
                <span className="l-muted shrink-0 font-mono text-[9px] uppercase tracking-widest">{ev.tag}</span>
              </div>
            );
          })}
        </div>
      </WindowFrame>
    </div>
  );
}

function AttendanceMock() {
  const [on, setOn] = useState(false);
  const [secs, setSecs] = useState(0);

  useEffect(() => {
    if (!on) return undefined;
    const t = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [on]);

  const fmt = (s) => {
    const h = String(Math.floor(s / 3600)).padStart(2, "0");
    const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
    const sec = String(s % 60).padStart(2, "0");
    return `${h}:${m}:${sec}`;
  };

  return (
    <WindowFrame title="attendance / gps clock in">
      <div className="l-bg l-line relative overflow-hidden border">
        <svg viewBox="0 0 300 170" className="w-full" style={{ color: "var(--l-line)" }}>
          <defs>
            <pattern id="ss-grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.8" />
            </pattern>
          </defs>
          <rect width="300" height="170" fill="url(#ss-grid)" />
          <circle cx="150" cy="85" r="55" fill="rgba(236,93,42,0.1)" stroke="#EC5D2A" strokeWidth="1.2" strokeDasharray="4 4" />
          {on && (
            <circle
              className="ss-pulse"
              cx="150"
              cy="85"
              r="10"
              fill="none"
              stroke="#22c55e"
              strokeWidth="2"
              style={{ transformOrigin: "center", transformBox: "fill-box" }}
            />
          )}
          <circle cx="150" cy="85" r="6" fill={on ? "#22c55e" : "#8a8a8a"} />
        </svg>
        <div className="l-surface l-line l-muted absolute left-3 top-3 border px-2 py-1 font-mono text-[9px] uppercase tracking-widest">
          <MapPin className="mr-1 inline h-3 w-3" style={{ color: "var(--l-accent)" }} />
          Geofence active
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div>
          <div className="l-muted font-mono text-[10px] uppercase tracking-widest">Status</div>
          <div className={`mt-1 text-sm font-semibold ${on ? "text-green-600" : ""}`}>{on ? "On site" : "Off site"}</div>
        </div>
        <div className="text-right">
          <div className="l-muted font-mono text-[10px] uppercase tracking-widest">Shift timer</div>
          <div className="mt-1 font-mono text-xl font-bold">{fmt(secs)}</div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => {
          if (on) setOn(false);
          else {
            setSecs(0);
            setOn(true);
          }
        }}
        className={`mt-4 w-full rounded-sm py-3 text-sm font-semibold transition-colors ${on ? "l-ghost" : "l-btn"}`}
      >
        {on ? "Clock out" : "Clock in"}
      </button>
    </WindowFrame>
  );
}

const CHECKPOINTS = [
  "1.1 Substrate is dry, clean and free from contamination",
  "1.2 Surface falls to drain are 1 in 80",
  "1.3 Barricades and signage are installed",
  "1.4 Photo evidence of membrane application",
];

function InspectionsMock() {
  const [answers, setAnswers] = useState([null, null, null, null]);

  const setAnswer = (idx, value) => {
    setAnswers((prev) => prev.map((a, i) => (i === idx ? (a === value ? null : value) : a)));
  };

  const pass = answers.filter((a) => a === "Pass").length;
  const fail = answers.filter((a) => a === "Fail").length;
  const na = answers.filter((a) => a === "N/A").length;
  const done = pass + fail + na === answers.length;

  const btn = (idx, value, activeClass) => (
    <button
      key={value}
      type="button"
      onClick={() => setAnswer(idx, value)}
      className={`rounded-sm px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors ${
        answers[idx] === value ? activeClass : "l-chip"
      }`}
    >
      {value}
    </button>
  );

  return (
    <WindowFrame title="inspections / itp execution">
      <div className="flex items-center gap-5">
        <Donut pass={pass} fail={fail} na={na} total={answers.length} />
        <div className="space-y-1.5 font-mono text-xs">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-green-500" /> Pass: {pass}
          </div>
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-red-500" /> Fail: {fail}
          </div>
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-neutral-500" /> N/A: {na}
          </div>
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "var(--l-line)" }} /> Open:{" "}
            {answers.length - pass - fail - na}
          </div>
        </div>
      </div>

      <div className="mt-5 space-y-2">
        {CHECKPOINTS.map((c, idx) => (
          <div key={c} className="l-bg l-line border p-3">
            <div className="text-xs">{c}</div>
            <div className="mt-2 flex gap-2">
              {btn(idx, "Pass", "bg-green-500 text-white")}
              {btn(idx, "Fail", "bg-red-500 text-white")}
              {btn(idx, "N/A", "bg-neutral-500 text-white")}
            </div>
          </div>
        ))}
      </div>

      <div
        className={`mt-4 flex items-center justify-between border px-3 py-2.5 text-xs transition-colors ${
          done ? "" : "l-bg l-line l-muted"
        }`}
        style={done ? { borderColor: "var(--l-accent)", backgroundColor: "rgba(236,93,42,0.08)" } : undefined}
      >
        <span className="flex items-center gap-2">
          <FileText className="h-4 w-4" style={{ color: done ? "var(--l-accent)" : "var(--l-muted)" }} />
          {done ? "Report ready to export" : "Answer every checkpoint to unlock the report"}
        </span>
        {done && <Download className="h-4 w-4" style={{ color: "var(--l-accent)" }} />}
      </div>
    </WindowFrame>
  );
}

function DailyLogMock() {
  const [submitted, setSubmitted] = useState(false);
  return (
    <WindowFrame title="daily logs / new entry">
      <div className="l-muted font-mono text-[10px] uppercase tracking-widest">Field notes</div>
      <div className="l-bg l-line mt-2 space-y-2 border p-3">
        <div className="h-2 w-11/12 rounded" style={{ backgroundColor: "var(--l-line)" }} />
        <div className="h-2 w-9/12 rounded" style={{ backgroundColor: "var(--l-line)" }} />
        <div className="h-2 w-10/12 rounded" style={{ backgroundColor: "var(--l-line)" }} />
        <div className="h-2 w-6/12 rounded" style={{ backgroundColor: "var(--l-line)" }} />
      </div>

      <div className="l-muted mt-4 font-mono text-[10px] uppercase tracking-widest">Photos</div>
      <div className="mt-2 grid grid-cols-3 gap-2">
        {[0, 1, 2].map((n) => (
          <div
            key={n}
            className="l-line grid aspect-square place-items-center border"
            style={{
              background: `linear-gradient(135deg, rgba(236,93,42,${0.16 + n * 0.08}), var(--l-alt))`,
            }}
          >
            <Camera className="l-muted h-5 w-5" />
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => setSubmitted((s) => !s)}
        className={`mt-4 flex w-full items-center justify-center gap-2 rounded-sm py-3 text-sm font-semibold transition-colors ${
          submitted ? "bg-green-600 text-white hover:bg-green-500" : "l-btn"
        }`}
      >
        {submitted ? (
          <>
            <CheckCircle2 className="h-4 w-4" /> Log submitted
          </>
        ) : (
          "Submit log"
        )}
      </button>
    </WindowFrame>
  );
}

const CHAT_SEED = [
  { who: "Supervisor", me: false, text: "Slab pour starts at 7. Confirm crews." },
  { who: "Crew lead", me: false, text: "Confirmed. Level 1 is ready." },
];
const CHAT_REPLIES = [
  "Photos uploaded to checkpoint 2.1",
  "Falls checked, all good to drain",
  "Barricades up, area restricted",
  "Inspection signed off as Pass",
];

function ChatMock() {
  const [messages, setMessages] = useState(CHAT_SEED);
  const [count, setCount] = useState(0);

  const send = () => {
    const text = CHAT_REPLIES[count % CHAT_REPLIES.length];
    setMessages((prev) => [...prev, { who: "You", me: true, text }].slice(-5));
    setCount((c) => c + 1);
  };

  return (
    <WindowFrame title="chat / level 1 wet areas">
      <div className="space-y-2">
        {messages.map((m, idx) => (
          <div key={`${idx}-${m.text}`} className={`ss-slide flex ${m.me ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-3 py-2 text-xs ${m.me ? "l-btn" : "l-bg l-line border"}`}>
              <div
                className={`mb-0.5 font-mono text-[9px] uppercase tracking-widest ${m.me ? "text-white/80" : "l-muted"}`}
              >
                {m.who}
              </div>
              {m.text}
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={send}
        className="l-ghost mt-4 flex w-full items-center justify-center gap-2 rounded-sm py-3 text-sm font-semibold transition-colors"
      >
        <Send className="h-4 w-4" /> Send site update
      </button>
    </WindowFrame>
  );
}

const ROSTER_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const ROSTER_CREWS = ["Crew A", "Crew B", "Crew C", "Crew D"];

function RosterMock() {
  const [cells, setCells] = useState(() => {
    const init = {};
    ROSTER_CREWS.forEach((c, ci) => {
      ROSTER_DAYS.forEach((d, di) => {
        init[`${c}-${d}`] = (ci + di) % 2 === 0;
      });
    });
    return init;
  });

  const toggle = (key) => setCells((prev) => ({ ...prev, [key]: !prev[key] }));
  const total = Object.values(cells).filter(Boolean).length;

  return (
    <WindowFrame title="roster / week planner">
      <div className="grid grid-cols-6 gap-1.5 text-center">
        <div />
        {ROSTER_DAYS.map((d) => (
          <div key={d} className="l-muted font-mono text-[10px] uppercase tracking-widest">
            {d}
          </div>
        ))}
        {ROSTER_CREWS.map((c) => (
          <div key={c} className="contents">
            <div className="l-muted flex items-center text-left font-mono text-[10px] uppercase tracking-widest">{c}</div>
            {ROSTER_DAYS.map((d) => {
              const key = `${c}-${d}`;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggle(key)}
                  aria-label={`${c} ${d}`}
                  className={`h-9 transition-colors ${cells[key] ? "l-btn" : "l-cell-off"}`}
                />
              );
            })}
          </div>
        ))}
      </div>
      <div className="l-bg l-line mt-4 flex items-center justify-between border px-3 py-2.5 text-xs">
        <span className="l-muted font-mono uppercase tracking-widest">Shifts planned</span>
        <span className="font-mono text-lg font-bold" style={{ color: "var(--l-accent)" }}>
          {total}
        </span>
      </div>
    </WindowFrame>
  );
}

const MOCKS = {
  attendance: AttendanceMock,
  inspections: InspectionsMock,
  logs: DailyLogMock,
  chat: ChatMock,
  roster: RosterMock,
};

/* ---------- formulario de contato ---------- */

const EMPTY_FORM = {
  topic: TOPICS[0],
  name: "",
  email: "",
  company: "",
  phone: "",
  message: "",
  website: "",
};

const FIELD_CLASS = "l-field w-full rounded-sm px-3 py-3 text-base outline-none transition-shadow";
const LABEL_CLASS = "l-muted mb-1.5 block font-mono text-[10px] font-bold uppercase tracking-[0.2em]";

function ContactForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const update = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const isDemo = form.topic === "Request a demo";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("sending");
    try {
      await api.post("/contact/demo-request", {
        topic: form.topic,
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim() || null,
        website: form.website,
      });
      setForm(EMPTY_FORM);
      setStatus("sent");
    } catch (err) {
      setStatus("idle");
      setError(formatApiError(err.response?.data?.detail, "Could not send your message. Please try again."));
    }
  };

  if (status === "sent") {
    return (
      <div className="ss-slide l-surface l-line border p-8 text-center">
        <CheckCircle2 className="mx-auto h-14 w-14 text-green-500" strokeWidth={1.5} />
        <h3 className="l-display mt-5 text-2xl">Message received</h3>
        <p className="l-muted mt-2">
          Thanks for getting in touch. Our team will get back to you within one business day.
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="l-ghost mt-6 rounded-sm px-5 py-2.5 text-sm font-semibold transition-colors"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="l-surface l-line relative border p-6 md:p-8" data-testid="contact-form">
      <div>
        <label htmlFor="contact-topic" className={LABEL_CLASS}>
          How can we help
        </label>
        <select id="contact-topic" value={form.topic} onChange={update("topic")} className={FIELD_CLASS}>
          {TOPICS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className={LABEL_CLASS}>
            Full name
          </label>
          <input
            id="contact-name"
            type="text"
            required
            minLength={2}
            maxLength={120}
            autoComplete="name"
            value={form.name}
            onChange={update("name")}
            placeholder="Your name"
            className={FIELD_CLASS}
          />
        </div>
        <div>
          <label htmlFor="contact-email" className={LABEL_CLASS}>
            Work email
          </label>
          <input
            id="contact-email"
            type="email"
            required
            autoComplete="email"
            value={form.email}
            onChange={update("email")}
            placeholder="you@company.com"
            className={FIELD_CLASS}
          />
        </div>
        <div>
          <label htmlFor="contact-company" className={LABEL_CLASS}>
            Company
          </label>
          <input
            id="contact-company"
            type="text"
            required
            minLength={2}
            maxLength={160}
            autoComplete="organization"
            value={form.company}
            onChange={update("company")}
            placeholder="Company name"
            className={FIELD_CLASS}
          />
        </div>
        <div>
          <label htmlFor="contact-phone" className={LABEL_CLASS}>
            Phone (optional)
          </label>
          <input
            id="contact-phone"
            type="tel"
            maxLength={40}
            autoComplete="tel"
            value={form.phone}
            onChange={update("phone")}
            placeholder="+61 400 000 000"
            className={FIELD_CLASS}
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-message" className={LABEL_CLASS}>
          {isDemo ? "What would you like to see?" : "Your message"}
        </label>
        <textarea
          id="contact-message"
          rows={4}
          maxLength={2000}
          value={form.message}
          onChange={update("message")}
          placeholder="Tell us about your team, your trade and what you want to improve on site"
          className={`${FIELD_CLASS} resize-y`}
        />
      </div>

      {/* Campo isca anti robo: invisivel para pessoas. */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
        <label htmlFor="contact-website">Website</label>
        <input
          id="contact-website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={update("website")}
        />
      </div>

      {error && (
        <div className="mt-4 border border-red-400 bg-red-500/10 px-3 py-2 text-sm text-red-600" data-testid="contact-error">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={status === "sending"}
        className="l-btn mt-6 flex w-full items-center justify-center gap-2 rounded-sm py-3.5 text-base font-semibold transition-colors disabled:opacity-60"
        data-testid="contact-submit"
      >
        {status === "sending" ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Sending
          </>
        ) : (
          <>
            {isDemo ? "Request a demo" : "Send message"} <ArrowRight className="h-4 w-4" />
          </>
        )}
      </button>
      <p className="l-muted mt-3 text-center text-xs">We only use your details to reply to this message.</p>
    </form>
  );
}

/* ---------- pagina ---------- */

export default function Landing() {
  const { theme, setTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [tab, setTab] = useState("inspections");
  const [role, setRole] = useState("Worker");
  const [openFaq, setOpenFaq] = useState(0);

  // Na primeira visita ao site, abre no modo claro. Depois disso, a escolha de
  // quem usa o botao de tema (claro ou escuro) e respeitada.
  useEffect(() => {
    try {
      if (!localStorage.getItem("ss_landing_theme_init")) {
        localStorage.setItem("ss_landing_theme_init", "1");
        setTheme("light");
      }
    } catch (e) {
      // sem acesso ao localStorage, segue com o tema atual
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const goTo = (id) => {
    setMenuOpen(false);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  const activeTab = TABS.find((t) => t.id === tab) || TABS[0];
  const Mock = MOCKS[activeTab.id];
  const activeRole = ROLES[role];
  const RoleIcon = activeRole.icon;

  return (
    <div className="ss-land min-h-screen">
      <style>{LANDING_CSS}</style>

      {/* Header */}
      <header className="l-surface l-line sticky top-0 z-40 border-b">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5">
          <Link to="/" aria-label="SiteSync home">
            <Wordmark />
          </Link>

          <nav className="hidden items-center gap-7 xl:flex">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => goTo(item.id)}
                className="l-hover text-[15px] font-medium transition-colors"
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              aria-label="Toggle light and dark mode"
              onClick={toggleTheme}
              className="l-chip hidden rounded-sm p-2.5 transition-colors sm:inline-flex"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <Link
              to="/login"
              className="l-ghost hidden rounded-sm px-4 py-2.5 text-[15px] font-semibold transition-colors md:inline-flex"
            >
              Log in
            </Link>
            <button
              type="button"
              onClick={() => goTo("contact")}
              className="l-btn rounded-sm px-4 py-2.5 text-[15px] font-semibold transition-colors"
            >
              Request a demo
            </button>
            <button
              type="button"
              aria-label="Open menu"
              onClick={() => setMenuOpen(true)}
              className="rounded-sm p-2 xl:hidden"
            >
              <Menu className="h-7 w-7" />
            </button>
          </div>
        </div>
      </header>

      {/* Menu em tela cheia (celular) */}
      {menuOpen && (
        <div className="l-surface fixed inset-0 z-50 flex flex-col xl:hidden">
          <div className="flex h-[72px] items-center justify-between px-5">
            <Wordmark />
            <button type="button" aria-label="Close menu" onClick={() => setMenuOpen(false)} className="rounded-sm p-2">
              <X className="h-7 w-7" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 pt-8">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => goTo(item.id)}
                className="flex w-full items-center justify-between py-5 text-left text-[28px] font-semibold tracking-tight"
              >
                {item.label}
                <ChevronRight className="h-6 w-6" />
              </button>
            ))}
            <Link
              to="/privacy"
              onClick={() => setMenuOpen(false)}
              className="flex w-full items-center justify-between py-5 text-[28px] font-semibold tracking-tight"
            >
              Privacy
              <ChevronRight className="h-6 w-6" />
            </Link>
          </div>

          <div className="l-bg space-y-1 px-6 py-6">
            <Link
              to="/login"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-4 py-3 text-2xl font-semibold"
            >
              <User className="h-6 w-6" /> Log in
            </Link>
            <Link
              to="/signup"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-4 py-3 text-2xl font-semibold"
            >
              <Building2 className="h-6 w-6" /> Create company
            </Link>
            <Link
              to="/join"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-4 py-3 text-2xl font-semibold"
            >
              <MapPin className="h-6 w-6" /> Join with invite
            </Link>
            <button type="button" onClick={toggleTheme} className="flex items-center gap-4 py-3 text-2xl font-semibold">
              {theme === "dark" ? <Sun className="h-6 w-6" /> : <Moon className="h-6 w-6" />}
              {theme === "dark" ? "Light mode" : "Dark mode"}
            </button>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="ss-dots l-alt l-line border-b">
        <div className="mx-auto max-w-7xl px-5 pt-12 md:pt-20">
          <Kicker>Meet SiteSync</Kicker>
          <h1 className="l-display mt-8 max-w-5xl text-[2.7rem] sm:text-6xl lg:text-[5.5rem]">
            The console for every <span className="ss-mark-word">jobsite</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-xl font-medium leading-snug md:text-2xl">
            See how construction teams run attendance, inspections and reporting from one connected platform.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4">
            <button
              type="button"
              onClick={() => goTo("contact")}
              className="l-btn inline-flex items-center gap-2 rounded-sm px-7 py-3.5 text-lg font-semibold transition-colors"
            >
              Request a demo <ArrowRight className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => goTo("tour")}
              className="l-hover inline-flex items-center gap-2 text-lg font-semibold transition-colors"
            >
              Try the product tour <ArrowRight className="h-5 w-5" />
            </button>
          </div>

          <div className="relative mt-12 pb-10 md:mt-16 lg:pb-14">
            <Photo
              srcs={IMG.hero}
              alt="Construction jobsite"
              art={<ArtSkyline />}
              className="aspect-[4/3] w-full sm:aspect-[16/8]"
            />
            <div className="mt-6 lg:absolute lg:bottom-0 lg:right-8 lg:mt-0 lg:w-[400px]">
              <LivePanel />
            </div>
          </div>
        </div>
      </section>

      {/* Especificacoes */}
      <section className="l-bg l-line border-b">
        <div className="l-line mx-auto grid max-w-7xl grid-cols-2 border-l md:grid-cols-4">
          {SPECS.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.k} className="l-line border-b border-r px-5 py-7 md:border-b-0">
                <Icon className="h-5 w-5" style={{ color: "var(--l-accent)" }} />
                <div className="mt-3 font-mono text-[12px] font-bold uppercase tracking-[0.16em]">{s.k}</div>
                <div className="l-muted mt-1 text-sm">{s.v}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pilares */}
      <section className="l-bg l-line border-b">
        <div className="mx-auto max-w-7xl px-5 py-16 md:py-28">
          <Reveal>
            <h2 className="l-display max-w-4xl text-4xl md:text-6xl">Better days for everyone on site start here</h2>
            <p className="l-muted mt-6 max-w-2xl text-xl leading-relaxed">
              Three ideas guide how we build SiteSync, from the first checkpoint to the final report.
            </p>
          </Reveal>

          <div className="mt-16 grid gap-14 md:grid-cols-3 md:gap-10">
            {PILLARS.map((p, idx) => (
              <Reveal key={p.title} delay={idx * 120}>
                <Picto kind={p.picto} />
                <h3 className="l-display mt-6 text-3xl">{p.title}</h3>
                <p className="l-muted mt-4 text-lg leading-relaxed">{p.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Tour do produto */}
      <section id="tour" className="l-alt l-line scroll-mt-20 border-b">
        <div className="mx-auto max-w-7xl px-5 py-16 md:py-28">
          <Reveal>
            <Kicker>Interactive product tour</Kicker>
            <h2 className="l-display mt-6 max-w-4xl text-4xl md:text-6xl">Do not read about it. Click it.</h2>
            <p className="l-muted mt-6 max-w-2xl text-xl leading-relaxed">
              Pick a module and play with the demo. These are the same flows your team uses every day.
            </p>
          </Reveal>

          <div className="mt-10 flex gap-2 overflow-x-auto pb-2">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = t.id === tab;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`flex shrink-0 items-center gap-2 rounded-sm px-4 py-2.5 text-[15px] font-semibold transition-colors ${
                    active ? "l-btn" : "l-surface l-chip"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {t.label}
                </button>
              );
            })}
          </div>

          <div className="mt-10 grid items-start gap-12 md:grid-cols-2">
            <div key={`info-${activeTab.id}`} className="ss-slide">
              <h3 className="l-display text-3xl md:text-4xl">{activeTab.title}</h3>
              <p className="l-muted mt-5 text-lg leading-relaxed">{activeTab.text}</p>
              <ul className="mt-7 space-y-3">
                {activeTab.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-lg">
                    <CheckCircle2 className="mt-1 h-5 w-5 shrink-0" style={{ color: "var(--l-accent)" }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div key={`mock-${activeTab.id}`} className="ss-slide">
              <Mock />
            </div>
          </div>
        </div>
      </section>

      {/* Beneficios com fotos */}
      <section id="benefits" className="l-bg l-line scroll-mt-20 border-b">
        <div className="mx-auto max-w-7xl px-5 py-16 md:py-28">
          <Reveal>
            <Kicker>Benefits</Kicker>
            <h2 className="l-display mt-6 max-w-4xl text-4xl md:text-6xl">One record from the site to the client</h2>
          </Reveal>

          <div className="mt-12">
            {STORIES.map((s, idx) => {
              const Art = ART[s.art];
              return (
                <Reveal key={s.kicker}>
                  <div className="l-line grid items-center gap-8 border-t py-12 md:grid-cols-2 md:gap-16">
                    <Photo
                      srcs={s.srcs}
                      alt={s.title}
                      art={<Art />}
                      className={`aspect-[4/3] w-full ${idx % 2 ? "md:order-2" : ""}`}
                    />
                    <div>
                      <Kicker>{s.kicker}</Kicker>
                      <h3 className="l-display mt-5 text-3xl md:text-5xl">{s.title}</h3>
                      <p className="l-muted mt-5 text-lg leading-relaxed md:text-xl">{s.text}</p>
                      <button
                        type="button"
                        onClick={() => goTo(s.link)}
                        className="l-hover mt-7 inline-flex items-center gap-2 text-lg font-semibold transition-colors"
                      >
                        {s.cta} <ArrowRight className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Como funciona */}
      <section id="how-it-works" className="l-alt l-line scroll-mt-20 border-b">
        <div className="mx-auto max-w-7xl px-5 py-16 md:py-28">
          <Reveal>
            <Kicker>How it works</Kicker>
            <h2 className="l-display mt-6 max-w-4xl text-4xl md:text-6xl">From setup to site report in four steps</h2>
          </Reveal>

          <div className="mt-14">
            {STEPS.map((step, idx) => (
              <Reveal key={step.n} delay={idx * 80}>
                <div className="l-line grid gap-3 border-t py-8 md:grid-cols-[140px_1fr_1.3fr] md:gap-10">
                  <span className="font-mono text-3xl font-bold" style={{ color: "var(--l-accent)" }}>
                    {step.n}
                  </span>
                  <h3 className="l-display text-2xl md:text-3xl">{step.title}</h3>
                  <p className="l-muted text-lg leading-relaxed">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Perfis, em faixa escura */}
      <section id="roles" className="scroll-mt-20 bg-[#0C1A35] text-[#F4F1EC]">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 py-16 md:py-28 lg:grid-cols-2">
          <div>
            <Kicker>Roles</Kicker>
            <h2 className="l-display mt-6 text-4xl md:text-6xl" style={{ color: "#F4F1EC" }}>
              Built for everyone who builds
            </h2>
            <p className="mt-6 max-w-xl text-xl leading-relaxed text-[#9DB0CC]">
              Each role sees the tools it needs, on the same platform and the same record.
            </p>

            <div className="mt-8 inline-flex rounded-full border border-[#223A66] p-1">
              {Object.keys(ROLES).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  className={`rounded-full px-5 py-2.5 text-[15px] font-semibold transition-colors ${
                    role === r ? "bg-[#EC5D2A] text-white" : "text-[#9DB0CC] hover:text-white"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>

            <div key={role} className="ss-slide mt-8">
              <div className="flex items-center gap-3">
                <RoleIcon className="h-7 w-7 text-[#EC5D2A]" strokeWidth={1.6} />
                <span className="text-lg font-semibold">{activeRole.summary}</span>
              </div>
              <ul className="mt-5 space-y-3">
                {activeRole.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 border-t border-[#223A66] pt-3 text-lg">
                    <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-[#EC5D2A]" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <DiamondCollage />
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="l-bg l-line scroll-mt-20 border-b">
        <div className="mx-auto max-w-4xl px-5 py-16 md:py-28">
          <Reveal>
            <Kicker>FAQ</Kicker>
            <h2 className="l-display mt-6 text-4xl md:text-6xl">Questions, answered</h2>
          </Reveal>

          <div className="mt-12">
            {FAQ.map((item, idx) => {
              const open = openFaq === idx;
              return (
                <div key={item.q} className="l-line border-t last:border-b">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(open ? -1 : idx)}
                    className="flex w-full items-center justify-between gap-4 py-6 text-left"
                  >
                    <span className="text-xl font-semibold">{item.q}</span>
                    <ChevronDown
                      className={`h-6 w-6 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                      style={{ color: "var(--l-accent)" }}
                    />
                  </button>
                  {open && <div className="ss-slide l-muted pb-6 text-lg leading-relaxed">{item.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact us */}
      <section id="contact" className="l-alt l-line scroll-mt-20 border-b">
        <div className="mx-auto max-w-7xl px-5 py-16 md:py-28">
          <Reveal>
            <Kicker>Contact us</Kicker>
            <h2 className="l-display mt-6 max-w-4xl text-4xl md:text-6xl">Request a demo or ask us anything</h2>
            <p className="l-muted mt-6 max-w-2xl text-xl leading-relaxed">
              Tell us about your team and we will show you SiteSync running on a setup like yours. No commitment.
            </p>
          </Reveal>

          <div className="mt-14 grid items-start gap-14 md:grid-cols-2">
            <Reveal>
              <div className="l-muted font-mono text-[11px] font-bold uppercase tracking-[0.2em]">What to expect</div>
              <ul className="mt-5 space-y-5">
                {CONTACT_POINTS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.text} className="flex items-start gap-4">
                      <div className="l-surface l-line grid h-11 w-11 shrink-0 place-items-center border">
                        <Icon className="h-5 w-5" style={{ color: "var(--l-accent)" }} />
                      </div>
                      <span className="pt-2.5 text-lg">{item.text}</span>
                    </li>
                  );
                })}
              </ul>
            </Reveal>

            <Reveal delay={120}>
              <ContactForm />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Faixa laranja */}
      <section style={{ backgroundColor: "var(--l-accent)" }}>
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-5 py-16 md:flex-row md:items-center">
          <div>
            <h2 className="l-display text-3xl md:text-5xl" style={{ color: "#FFFFFF" }}>
              Ready to run your jobsites on SiteSync?
            </h2>
            <p className="mt-3 text-lg text-white/90">Request a demo, or log in to your console right away.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => goTo("contact")}
              className="rounded-sm bg-black px-7 py-3.5 text-lg font-semibold text-white transition-colors hover:bg-neutral-800"
            >
              Contact us
            </button>
            <Link
              to="/login"
              className="rounded-sm border border-white/80 px-7 py-3.5 text-lg font-semibold text-white transition-colors hover:bg-white/10"
            >
              Log in
            </Link>
          </div>
        </div>
      </section>

      {/* Rodape */}
      <footer className="bg-[#0C1A35] text-[#F4F1EC]">
        <div className="mx-auto max-w-7xl px-5 pb-28 pt-14 md:pb-12">
          <div className="grid gap-10 md:grid-cols-4">
            <div>
              <Wordmark light />
              <p className="mt-5 max-w-xs text-[15px] leading-relaxed text-[#9DB0CC]">
                The console for every jobsite. Attendance, inspections, logs, chat and rosters in one place.
              </p>
            </div>

            <div>
              <div className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#9DB0CC]">Product</div>
              <ul className="mt-4 space-y-2.5 text-[15px]">
                <li>
                  <button type="button" onClick={() => goTo("tour")} className="hover:text-[#EC5D2A]">
                    Product tour
                  </button>
                </li>
                <li>
                  <button type="button" onClick={() => goTo("benefits")} className="hover:text-[#EC5D2A]">
                    Benefits
                  </button>
                </li>
                <li>
                  <button type="button" onClick={() => goTo("how-it-works")} className="hover:text-[#EC5D2A]">
                    How it works
                  </button>
                </li>
                <li>
                  <button type="button" onClick={() => goTo("roles")} className="hover:text-[#EC5D2A]">
                    Roles
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <div className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#9DB0CC]">Company</div>
              <ul className="mt-4 space-y-2.5 text-[15px]">
                <li>
                  <button type="button" onClick={() => goTo("contact")} className="hover:text-[#EC5D2A]">
                    Contact us
                  </button>
                </li>
                <li>
                  <button type="button" onClick={() => goTo("faq")} className="hover:text-[#EC5D2A]">
                    FAQ
                  </button>
                </li>
                <li>
                  <Link to="/privacy" className="hover:text-[#EC5D2A]">
                    Privacy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="hover:text-[#EC5D2A]">
                    Terms
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <div className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#9DB0CC]">Access</div>
              <ul className="mt-4 space-y-2.5 text-[15px]">
                <li>
                  <Link to="/login" className="hover:text-[#EC5D2A]">
                    Log in
                  </Link>
                </li>
                <li>
                  <Link to="/signup" className="hover:text-[#EC5D2A]">
                    Create company
                  </Link>
                </li>
                <li>
                  <Link to="/join" className="hover:text-[#EC5D2A]">
                    Join with invite
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 border-t border-[#223A66] pt-6 font-mono text-xs uppercase tracking-widest text-[#9DB0CC]">
            {new Date().getFullYear()} SiteSync. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Barra fixa no celular, como a da Procore */}
      <nav
        className="l-surface l-line fixed inset-x-0 bottom-0 z-40 grid grid-cols-3 border-t md:hidden"
        aria-label="Quick actions"
      >
        <button type="button" onClick={() => goTo("contact")} className="flex flex-col items-center gap-1 py-3 text-sm">
          <Mail className="h-5 w-5" />
          Contact
        </button>
        <button type="button" onClick={() => goTo("tour")} className="flex flex-col items-center gap-1 py-3 text-sm">
          <PlayCircle className="h-5 w-5" />
          Tour
        </button>
        <Link to="/login" className="flex flex-col items-center gap-1 py-3 text-sm">
          <User className="h-5 w-5" />
          Login
        </Link>
      </nav>
    </div>
  );
}
